import React, { Fragment, useState, useEffect, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import lang from '@/lang'
import { fetchSearchResults, resetSearch } from '@/redux/ducks/search/search'

import PerfectScrollbar from 'react-perfect-scrollbar'
import { TextInput } from '@/modules/base/components/inputs/index'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import SearchResultItem from '@/modules/base/components/search/search-result-item/search-result-item'
import EmptyStateIcon from '@assets/images/emptyStates/globalSearch.svg'
import { useAppDispatch, useAppSelector } from '@/redux/store'

const useStyles = makeStyles(theme => ({
  container: {
    padding: '25px 30px',
  },
  searchWrapper: {
    borderBottom: `1px solid ${theme.mode.borderColor.light}`,
  },
  searchInput: {
    '& input': {
      fontSize: 17,
      height: 54,
      background: theme.mode.backgroundColor.default,
      color: theme.mode.color.default,
      border: `1px solid ${theme.mode.borderColor.light}`,
      '&:focus': {
        background: theme.mode.backgroundColor.default,
        color: theme.mode.color.default,
        borderColor: theme.primary,
      },
      '&::placeholder': {
        color: theme.mode.color.light,
      },
    },
  },
  resultWrapper: {
    height: 'calc(100% - 105px)',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: theme.mode.color.default,
    '& > svg': {
      height: 'fit-content',
    },
  },
  emptyStateText: {
    fontSize: 17,
    marginTop: 20,
    textAlign: 'center',
    width: 210,
  },
  headline: {
    color: theme.mode.color.default,
    backgroundColor: theme.mode.backgroundColor.default,
    fontSize: 17,
  },
  [theme.devices.mobile]: {
    '& $container': {
      width: '100%',
    },
  },
}))

const GlobalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const { isLoading, searchResults } = useAppSelector(state => state.search.search)
  const dispatch = useAppDispatch()
  const classes = useStyles()

  const onSearch = useCallback(debounce((value) => {
    setSearchTerm(value)
    dispatch(fetchSearchResults(value))
  }, 300), [searchTerm])

  useEffect(() => dispatch(resetSearch()), [])

  const emptyState = (
    <div className={clsx(classes.resultWrapper, classes.emptyState)}>
      <SVGIcon width='200px' svg={EmptyStateIcon} />
      <div className={classes.emptyStateText}>{lang.globalSearch.emptyStateText}</div>
    </div>
  )

  const results = Object.keys(searchResults).reduce((results, category, index) => {
    if (!searchResults[category] || !searchResults[category].length) return results

    results.push((
      <Fragment key={`${category}-${index}`}>
        <div className={clsx(classes.container, classes.headline)}>
          {lang.globalSearch.searchCategories[category]} {`(${searchResults[category].length})`}
        </div>
        <div>
          {searchResults[category].map((result, index) => {
            return <SearchResultItem key={`${category}-result-${index}`} result={result} />
          })}
        </div>
      </Fragment>
    ))

    return results
  }, [])

  return (
    <Fragment>
      <div className={clsx(classes.container, classes.searchWrapper)}>
        <TextInput
          autoFocus={true}
          enableAiFeatures={false}
          name='searchTerm'
          inputId='global-search-input'
          dataQsId='global-search-input'
          placeholder={lang.globalSearch.placeholder}
          customClass={classes.searchInput}
          value={searchTerm}
          onChange={onSearch}
          autoComplete='off'
          useFormik={false}
        />
      </div>
      <div className={classes.resultWrapper}>
        <PerfectScrollbar component="div">
          {(Object.keys(searchResults).length === 0 && searchTerm.length >= 2 && !isLoading)
            ? emptyState
            : results
          }
        </PerfectScrollbar>
      </div>
    </Fragment>
  )
}

export default GlobalSearch
